import type * as React from "react";
import { memo, type ReactNode, useEffect, useMemo } from "react";

import { useSelector } from "react-redux";
import Parameter from "../../../../common/components/Parameter";
import classNames from "classnames";
import DateInput from "../../../../common/components/DateInput";
import { CURRENCY_ISO, MAX_QUANTITY_LENGTH } from "../../../../utils/constants";
import { isOfferPriceValid, isValidQuantity } from "../../../../utils/packageEntryUtils";
import { blurOfferPrice, updateInternalComment, updateOfferPrice, updateTargetPrice } from "../../../redux/actions";
import DeleteButton from "../../../../Cart/components/CartPage/ProductTable/DeleteButton";
import { CommentButton } from "./CommentButton";

import PriceInput from "../../../../ProductDetails/components/AddToCartCard/SalesmanAddToCartCard/PriceInput";
import { type PriceData } from "../../../../models/PriceData";
import * as PriceUtils from "../../../../utils/PriceUtils";
import { zeroNetPrice } from "../../../../utils/PriceUtils";
import { selectTotalTargetPriceForPackage } from "../../../redux/activeQuoteSelectors";
import CartQuantityInput from "../../../../Cart/components/CartPage/ProductTable/CartQuantityInput";
import { ActiveQuoteShowInternalContext } from "../ActiveQuoteEdit";
import { type LineItem } from "../../../../Cart/models/CartData";
import SimplePriceText, { PriceTextVariants } from "../../../../common/components/SimplePriceText/SimplePriceText";
import OptimeSolutionsMessage from "../../../../common/components/Messages/OptimeSolutionsMessage";
import { canUseCommentsForProduct } from "../../../../utils/productUtils";
import { EditablePackagingCell } from "../../../../Cart/components/CartPage/ProductTable/EditableEntry/PackagingCell";
import { Card } from "../../../../Configurators/Linear/components/Card";
import { AliasesParameter } from "../../../../common/components/Product/ProductParams";
import {
  selectLineItem,
  selectProduct,
  useAbstractLineItemActions,
} from "../../../../Cart/components/CartPage/ProductTable/EditableEntry/EditablePackageEntry";
import { isSpecialPrice } from "../../../../ProductDetails/redux/productDetailsSelectors";
import { useOnMount } from "../../../../common/hooks/useOnMount";
import { selectPackaging, toggleShowPackaging } from "../../../../Cart/redux/actions/cartPageActions";
import { Collapse, Stack, styled } from "@mui/material";
import CenterContents from "../../../../common/components/CenterContents";
import { PackagingSelection } from "../../../../common/components/PackagingSelection/PackagingSelection";
import CircleSpinner from "../../../../common/components/CircleSpinner/CircleSpinner";
import { C_SURFACE } from "../../../../common/components/icon/Colors";
import { getTotalPrice, isProductPMA } from "../../../../feature/ProductDescriptor/v1/productDescriptorUtils";
import { getVisibleNetPrice } from "../../../../Cart/redux/selectors/productEntrySelectors";
import { obtainSpecialPriceLabel } from "~/common/components/Price/v1/Price";
import { useTranslation } from "react-i18next";
import { InfoProductMessagePMA } from "~/ProductDetails/components/ProductSummary/InfoProductMessagePMA";
import { PackagingSelectionVariant } from "~/common/components/PackagingSelection/PackagingSelectionVariant";
import { canSeePriceRefColumn } from "~/QuickEntryPopup/helpers";
import { PriceReferenceParam } from "~/Cart/components/LineItemEditableParams";
import { Stacker } from "~/common/components/Stacker/Stacker";

import classes from "./EditablePackageEntry.module.less";
import { FCCTag } from "~/FreeCapacityPush/components/FCCTag/FCCTag";
import { CommentInput } from "~/common/components/CommentInput/CommentInput";
import type { NumberFormatEvent } from "~/common/components/CustomNumberFormat";
import { Alert } from "~/common/m3/Alert/Alert";
import type { ProductDescriptor } from "~/feature/ProductDescriptor/v1/types";

interface SelfProps {
  packEntryId: number;
  index: number;
  onRender?: () => void;
  className?: string;
}

const CellRow = ({ children }: { children?: ReactNode }) => (
  <div className={"scha__active-quote__product-table__right-side__col-container"}>{children}</div>
);

const Cell: React.FunctionComponent<{
  alignEnd?: boolean;
  first?: boolean;
  last?: boolean;
  className?: string;
  children?: ReactNode;
}> = ({ children, alignEnd, first, last, className }) => {
  const cls = classNames(
    "scha__cart__product-table__body-cell",
    {
      "first-on-right": first,
      "last-on-right": last,
      "flex-justify-end d-flex": alignEnd,
    },
    className,
  );
  return <div className={cls}>{children}</div>;
};

const Row: React.FunctionComponent<{ className?: string; children?: ReactNode }> = ({ children, className }) => (
  <div className={classNames("d-flex", className)}>{children}</div>
);

const useSalesmanLineItemActions = (lineItem: LineItem, product: ProductDescriptor) => {
  const { actions, dataRef, dispatch } = useAbstractLineItemActions(lineItem, product);

  const salesmanActions = useMemo(() => {
    const packEntryId = dataRef.current.lineItem.id;
    return {
      updateInternalComment: (comment: string) => dispatch(updateInternalComment(comment, packEntryId)),
      updateOfferPrice: (e?: NumberFormatEvent) =>
        dispatch(
          updateOfferPrice(packEntryId, e ? { value: e.floatValue ?? 0, currencyIso: CURRENCY_ISO } : undefined),
        ),
      blurOfferPrice: () => dispatch(blurOfferPrice(packEntryId)),
      updateTargetPrice: (e?: NumberFormatEvent) =>
        dispatch(
          updateTargetPrice(packEntryId, e ? { value: e.floatValue ?? 0, currencyIso: CURRENCY_ISO } : undefined),
        ),
      toggleShowPackaging: () => dispatch(toggleShowPackaging(packEntryId)),
      closePackaging: () => dispatch(toggleShowPackaging(packEntryId, false)),
      selectPackage: (packCode: string) => dispatch(selectPackaging(packEntryId, packCode)),
    };
  }, []);

  return {
    ...actions,
    ...salesmanActions,
    onPackagingClick: salesmanActions.toggleShowPackaging,
    selectPackage: salesmanActions.selectPackage,
    closePackaging: salesmanActions.closePackaging,
  };
};

const EditableLineItem = ({ packEntryId, index, onRender }: SelfProps) => {
  const { t } = useTranslation();
  const packageEntry = useSelector(selectLineItem(packEntryId));
  const product = useSelector(selectProduct(packEntryId));

  const validQty = isValidQuantity(packageEntry.packSize, packageEntry.quantity);

  const messages = packageEntry.messages.slice();

  const tLabel = (key: string) => t(`activeQuotePage.table.content.${key}`);
  const isCommentVisible = packageEntry.showComments;
  const quantityVisible = product.base.quantityVisible;
  const isOptime = product.base.isOptime;

  const visiblePrice = getVisibleNetPrice(packageEntry, product) ?? zeroNetPrice(product);

  let offerPriceValid = true;
  if (packageEntry.priceOverriden && packageEntry.offerPrice) {
    offerPriceValid = isOfferPriceValid(packageEntry.offerPrice, packageEntry.msp);
  }

  const formatSalesPrice = (val?: PriceData) => (val ? PriceUtils.nonVolatileText(val) : "-");

  const priceUnit = visiblePrice.unit;
  const total = getTotalPrice(visiblePrice, packageEntry.quantity);

  const targetTotal = selectTotalTargetPriceForPackage(packageEntry);

  const canSeeExternalComments = canUseCommentsForProduct(product.base.marketType);

  const {
    isDayDisabled,
    updateDate,
    updateComment,
    toggleShowComment,
    updateQty,
    removeEntry,
    updateInternalComment,
    updateTargetPrice,
    updateOfferPrice,
    blurOfferPrice,
    onPackagingClick,
    selectPackage,
    updatePriceReference,
  } = useSalesmanLineItemActions(packageEntry, product);

  const disableEditing = !!packageEntry.messages.find((msg) => msg.type === "fatal");

  useOnMount(() => {
    if (!product.base.quantityVisible) {
      updateDate(new Date());
    }
  });

  useEffect(() => {
    if (onRender) {
      onRender();
    }
  }, [packageEntry, product]);

  const packageentryquantityinput = `package-entry-quantity-input-${index}`;

  const isSpecial = isSpecialPrice(product, packageEntry.packCode);
  const cspLabel = obtainSpecialPriceLabel(t);

  const isFcc = product.base.fifteenDigit[packageEntry.packCode]?.isFcc;

  return (
    <ActiveQuoteShowInternalContext.Consumer>
      {(showDetails) => (
        <Card className={"mb-2 qpackageentry_" + index} disableHover={true}>
          <div className={"py-3"}>
            <CellRow>
              <Cell first={true} className={classes.qtyCell}>
                {quantityVisible ? (
                  <CartQuantityInput
                    className={"w-100 qquantity"}
                    label={t("input.quantity")}
                    value={packageEntry.quantity}
                    onChange={updateQty}
                    allowedLength={MAX_QUANTITY_LENGTH}
                    error={!validQty}
                    data-test-id={packageentryquantityinput}
                    step={packageEntry.packSize}
                    disabled={disableEditing}
                  />
                ) : (
                  <Parameter
                    label={t("input.quantity")}
                    value={packageEntry.quantity}
                    data-test-id={"package-entry-quantity"}
                  />
                )}

                {isFcc && <FCCTag anchor={"left"} className={classes.fccTag} />}
              </Cell>

              <Cell className={"d-flex flex-direction-column"}>
                <EditablePackagingCell product={product} lineItem={packageEntry} openEdit={onPackagingClick} />
                <AliasesParameter
                  className={"mt-3"}
                  product={product.base}
                  mode={"15-digits"}
                  packCode={packageEntry.packCode}
                />
              </Cell>

              <Cell>
                {quantityVisible ? (
                  <DateInput
                    label={tLabel("chooseDate")}
                    data-test-id={"btn-edit-date"}
                    date={packageEntry.deliveryDate || null}
                    onChange={updateDate}
                    className={"quote_date_input quote_date_field"}
                    isInvalid={!packageEntry.deliveryDate}
                    isDayDisabled={isDayDisabled}
                    disabled={disableEditing}
                  />
                ) : (
                  <div>{"-"}</div>
                )}
              </Cell>

              <Cell>
                <Stacker gap={8}>
                  <PriceInput
                    data-test-id={"package-entry-input-offer-price"}
                    label={t("addProduct.salesman.offerPrice")}
                    value={visiblePrice?.value}
                    onChange={updateOfferPrice}
                    onBlur={blurOfferPrice}
                    error={showDetails && !offerPriceValid}
                    helperLabel={
                      <>
                        <SimplePriceText
                          priceUnit={priceUnit}
                          variant={quantityVisible ? null : PriceTextVariants.monthlySubsription}
                        />
                        {isSpecial && <div className={"scha__customer-special-price"}>{cspLabel}</div>}
                      </>
                    }
                  />

                  <PriceReferenceParam
                    product={product.base}
                    priceReference={packageEntry.priceReference}
                    onChange={updatePriceReference}
                    data-test-id={"package-entry-price-reference"}
                    disabled={disableEditing}
                    // disabled={disableEditing || saleActive}
                    editable={!packageEntry.quoteNumber && canSeePriceRefColumn()}
                  />
                </Stacker>
              </Cell>

              <Cell className={"d-flex flex-direction-column"}>
                <strong>{PriceUtils.text(total)}</strong>
                {!quantityVisible && (
                  <div className={"mt-1 f-label-color"}>
                    <SimplePriceText
                      priceUnit={priceUnit}
                      variant={quantityVisible ? null : PriceTextVariants.yearlySubscription}
                    />
                  </div>
                )}
              </Cell>

              {showDetails && (
                <>
                  <Cell className={"scha__active-quote__internal-cell d-flex flex-direction-column"}>
                    <div className="scha__active-quote__internal-cell__bg" />
                    <div className="mt-0 mb-1">
                      <span className={"f-label-color mr-2"}>{t("activeQuotePage.table.label.csp")}</span>
                      <span data-test-id={"package-entry-net-price"}>{formatSalesPrice(packageEntry.netPrice)}</span>
                    </div>
                    <div className="my-1 mb-0">
                      <span className={"f-label-color mr-2"}>{t("activeQuotePage.table.label.rsp")}</span>
                      <span data-test-id={"package-entry-rsp"}>{formatSalesPrice(packageEntry.rsp)}</span>
                    </div>
                    {/* <div className="mt-1 mb-0">
                      <span className={"f-label-color mr-2"}>{t("activeQuotePage.table.label.msp")}</span>
                      <span>{formatSalesPrice(packageEntry.msp)}</span>
                    </div> */}
                  </Cell>

                  <Cell className={"scha__active-quote__internal-cell"}>
                    <div className="scha__active-quote__internal-cell__bg" />
                    <PriceInput
                      label={t("addProduct.salesman.targetPrice")}
                      data-test-id={"package-entry-input-target-price"}
                      value={packageEntry.targetPrice?.value}
                      onChange={updateTargetPrice}
                      helperLabel={
                        <SimplePriceText
                          priceUnit={priceUnit}
                          variant={quantityVisible ? null : PriceTextVariants.monthlySubsription}
                        />
                      }
                    />
                  </Cell>

                  <Cell className={"scha__active-quote__internal-cell"}>
                    <div className="scha__active-quote__internal-cell__bg" />
                    <strong data-test-id={"package-entry-total-price"}>{PriceUtils.text(targetTotal)}</strong>
                  </Cell>
                </>
              )}

              <Cell alignEnd={true} last={true}>
                <Stacker gap={16} direction={"row"}>
                  <CommentButton
                    onClick={toggleShowComment}
                    open={packageEntry.showComments}
                    fontSize={"small"}
                    className={"scha__saved-configurations__comment-button"}
                  />
                  <DeleteButton onClick={removeEntry} disabled={!packageEntry.original} />
                </Stacker>
              </Cell>
            </CellRow>

            {isCommentVisible && (
              <Row className={"mt-3"}>
                <CommentInput
                  className={"pl-3 pr-2 scha__active-quote__product-table__comment-input"}
                  label={t("activeQuotePage.comment.title")}
                  value={!canSeeExternalComments ? "" : packageEntry.comment || ""}
                  onChange={updateComment}
                  disabled={!canSeeExternalComments}
                />
                {showDetails && (
                  <div className={"pl-2 pr-3 scha__active-quote__product-table__internal-comment-input"}>
                    <div className="scha__active-quote__product-table__internal-comment-input__bg" />
                    <CommentInput
                      label={t("activeQuotePage.internalComment.title")}
                      value={packageEntry.internalComment || ""}
                      onChange={updateInternalComment}
                    />
                  </div>
                )}
              </Row>
            )}
            {/* spacer without border */}
            {(!quantityVisible || isOptime || messages.length > 0) && <div className={"mt-2 mb-0"} />}
            <Stack gap={1} padding={2}>
              {messages.length > 0 && (
                <div className="d-flex flex-wrap">
                  {messages.map((message) => (
                    <Alert
                      key={message.key}
                      severity={message.type === "fatal" ? "error" : message.type}
                      className="w-100"
                    >
                      {t(message.key)}
                    </Alert>
                  ))}
                </div>
              )}
              {isProductPMA(product) && <InfoProductMessagePMA />}
              {!quantityVisible && <Alert severity="info">{t("subscriptionProduct.DST.billingInfo")}</Alert>}
              {isOptime && <OptimeSolutionsMessage />}
            </Stack>
          </div>
          <Collapse in={packageEntry.isPackagingSelectionOpen}>
            <PackagingArea disableHover={true}>
              {product.isLoading && (
                <CenterContents className={"my-30"}>
                  <CircleSpinner />
                </CenterContents>
              )}
              {product.isError && <CenterContents className={"my-30"}>{t("hybrisAjaxError")}</CenterContents>}
              {product.isLoaded && (
                <PackagingSelection
                  selectedCode={packageEntry.packCode}
                  onSelectCode={selectPackage}
                  product={product}
                  variant={PackagingSelectionVariant.SRP}
                />
              )}
            </PackagingArea>
          </Collapse>
        </Card>
      )}
    </ActiveQuoteShowInternalContext.Consumer>
  );
};

export default memo(EditableLineItem);

export const PackagingArea = styled(Card)({
  background: C_SURFACE,
  padding: "20px",
});
